/**
 * Row-kind predicates and bulk-target collection for an outline board. Every checkbox is self-only,
 * so nothing here rolls a line up from its tasks. Pure functions — the entity pair arrives as an
 * explicit argument, never by injection, so both the live and template outlines call the same code.
 */
import { OutlineEntityKinds, OutlineRow } from './outline.authoring.model';

/**
 * The row fields selection reads. Partial because AG Grid nodes carry whatever the projection sent,
 * and the owning-line reference is reached through `kinds.lineRef` rather than a named field.
 */
export type OutlineSelectionRow = Partial<OutlineRow>;

export function isOutlineServiceLine(row: OutlineSelectionRow | null | undefined, kinds: OutlineEntityKinds): boolean {

  return row?.sourceEntity === kinds.line;
}

export function isOutlineTask(row: OutlineSelectionRow | null | undefined, kinds: OutlineEntityKinds): boolean {

  return row?.sourceEntity === kinds.task;
}

/** Root task under a line. Lines have no schedule; subtasks inherit. */
export function isOutlineRootTask(row: OutlineSelectionRow | null | undefined, kinds: OutlineEntityKinds): boolean {

  return isOutlineTask(row, kinds) && row?.parentId?.startsWith(kinds.linePrefix) === true;
}

export function isOutlineSubtask(row: OutlineSelectionRow | null | undefined, kinds: OutlineEntityKinds): boolean {

  return isOutlineTask(row, kinds) && row?.parentId?.startsWith(kinds.taskPrefix) === true;
}


/** Task ids for bulk move/delete. Skip subtasks whose parent task is also selected — they ride with the parent. */
export function outlineMoveTaskIds(rows: readonly OutlineSelectionRow[], kinds: OutlineEntityKinds): string[] {

  const tasks = rows.filter(row => isOutlineTask(row, kinds) && !!row.sourceId);
  const selected = new Set(tasks.map(task => task.sourceId));
  return tasks
    .filter(task => {
      const parentTaskId = task.parentId?.startsWith(kinds.taskPrefix) === true ? task.parentId.slice(kinds.taskPrefix.length) : null;
      return !parentTaskId || !selected.has(parentTaskId);
    })
    .map(task => task.sourceId as string);
}

export function outlineSourceLineIds(rows: readonly OutlineSelectionRow[], kinds: OutlineEntityKinds): string[] {

  const ids = new Set<string>();
  for (const row of rows) {
    if (!isOutlineTask(row, kinds)) continue;
    const lineId = kinds.lineRef(row)?._id;
    if (lineId) ids.add(lineId);
  }
  return [...ids];
}
